import { apiClient } from "@/lib/api/client";
import type { SelectedPersonOption } from "@/routes/_staff/settings/persons/_list/_account/-types";
import { assign, fromPromise, setup } from "xstate";

type SearchPersonsOutput = SelectedPersonOption[];
type SearchPersonsInput = { searchText: string };

type GetPersonOutput = SelectedPersonOption;
type GetPersonInput = { personId: string };
const searchPersonsActor = fromPromise<SearchPersonsOutput, SearchPersonsInput>(
  async ({ input }) => {
    try {
      const res = await apiClient.GET("/v2/workers-for-select", {
        params: { query: { freeText: input.searchText, pageSize: 5 } },
      });
      if (!res.data?.items) return [];
      return res.data.items.map((worker) => ({
        id: worker.personId,
        value: worker.personId,
        label: worker.name,
        email: worker.primaryWorkerEmail,
        avatarUrl: worker.avatarUrl,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "";
      throw new Error(errorMessage);
    }
  },
);

const getPersonActor = fromPromise<GetPersonOutput, GetPersonInput>(
  async ({ input }) => {
    try {
      const res = await apiClient.GET("/v2/workers-for-select", {
        params: { query: { personIdIn: [input.personId] } },
      });
      if (!res.data?.items?.length) throw new Error("Person not found");
      const worker = res.data.items[0];
      return {
        id: worker.personId,
        value: worker.personId,
        label: worker.name,
        email: worker.primaryWorkerEmail,
        avatarUrl: worker.avatarUrl,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "";
      throw new Error(errorMessage);
    }
  },
);

type Context = {
  selectedPersonId: string;
  selectedPersonOption: SelectedPersonOption | null;
  searchText: string;
  searchResults: SelectedPersonOption[];
  errorMessage: string | null;
};

type Event =
  | { type: "INIT" }
  | { type: "GET_PERSON"; personId: string }
  | { type: "SEARCH_PERSON"; text: string }
  | {
      type: "SELECT_PERSON";
      personId: string;
      personOption: SelectedPersonOption;
    }
  | { type: "REMOVE_PERSON"; personId: string };

type Input = {
  selectedPersonId?: string;
};

const initialContext: Context = {
  selectedPersonId: "",
  selectedPersonOption: null,
  searchText: "",
  searchResults: [],
  errorMessage: null,
};

export const createPersonSelectMachine = () => {
  return setup({
    types: {
      context: {} as Context,
      events: {} as Event,
      input: {} as Input,
    },
    actors: {
      searchPersons: searchPersonsActor,
      getPerson: getPersonActor,
    },
    actions: {},
    guards: {
      isInitializedWithPersonId: ({ context }) =>
        !!context.selectedPersonId && context.selectedPersonOption == null,
    },
  }).createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5SzAQwE4GMAWAFM6sA9gHYB0AliRQC4DEAkgHIMAqA2gAwC6ioADkVi0KpPiAAeiAIwAmAKxkAnNM7yls2QA5ZnLRoA0IAJ6JZAZgBsZeZ0sB2VbIAs0+5dmWtAX29GUGDj4hKSU1PTMbOzSvEgggsI0oiTiUghyiipqGtq6+rJGpgha0mTSJVrOSu5eskryzr7+aFh4BMTkKAA2YJhJJFDBHXQAygCiAIIASgDCABIA+rhjUyMA8kxcsQJCImJxaZbyiqruVp7meuqFiOZa5mT38vZ3deae5Y1+IAGtQ6HdXr9QbtUijMYAGTGM1YSxW602PHECT2KQOiC0RxsWnuei07mcznuNwQsjcZHc0ks9Vs7nk0juTR+LSCoM6YB6fSoIJCJDoUzGAFk1gA1MZw1YbLbI3ZJfagNKY2RlezPczSZyaSycZz2ElyB62Tx2ZyWcxEqxKJm-Vm8sgwGj-PkQUhgMIANyIAGs3Ta2naHU6EFRPZhUHKSFtpXEURHUohLESyJxpBkGZxzOoySTdKVLvY1BqSpYvF5rSz-R17WBHWy6AR0ER0GR+F1wwAzJsAWzIfqd1drvODJFD4eSUaRMdlyXjCETWmTqYU6czGmkOY0Nkc5iURPxpvknnLgUrAIr3KddBdJDdIe9vor-b9F7Zw9HEYn23i0-lkgTSZTNNLlXbMTEQXUF04XcdBecx7E1eovmaE8n3PAZLwbJsWzbGhO3QHs+zZXs0J5Do3yIMMPx4aMdkSGd0TnACl3pYCs3XMCEHkakyBAnV3jkVV5F8b4SCICA4HEQjeRlOjfzSABaSwSUU48-iIqhaBk1FZ01El8TIXV7BUeCcWkDRd1U20q0BLl0LZLS4wY-FOBsBQrHMTMoNsZwSXVexk10exKmOcozOkSzT3IQN7KnWS0QVcDLgpVMjmkBpMy8JQcwZZN5DuPQC1NBCItQk8X2k2LtIY1wXPUNKSg85xznYopnE4ZUNA8MkaTazwvl8IA */
    id: "searchPerson",
    initial: "init",
    context: ({ input }) => ({
      ...initialContext,
      selectedPersonId: input.selectedPersonId ?? "",
    }),
    states: {
      init: {
        on: {
          INIT: [
            {
              guard: "isInitializedWithPersonId",
              target: "getPerson",
            },
            {
              target: "selectingPerson",
            },
          ],
        },
      },
      selectingPerson: {
        on: {
          SEARCH_PERSON: {
            actions: assign({
              searchText: ({ event }) => event.text,
            }),
            target: "searchingPerson",
          },
          SELECT_PERSON: {
            actions: assign({
              selectedPersonId: ({ event }) => event.personId,
              selectedPersonOption: ({ event }) => event.personOption,
              searchText: "",
              searchResults: [],
              errorMessage: null,
            }),
          },
          REMOVE_PERSON: {
            actions: assign({
              selectedPersonId: () => "",
              selectedPersonOption: null,
              errorMessage: null,
            }),
          },
          GET_PERSON: {
            target: "getPerson",
            actions: assign({
              selectedPersonId: ({ event }) => event.personId,
            }),
          },
        },
      },
      getPerson: {
        invoke: {
          src: "getPerson",
          input: ({ context }) => ({
            personId: context.selectedPersonId,
          }),
          onDone: [
            {
              target: "selectingPerson",
              actions: assign({
                selectedPersonOption: ({ event }) => event.output,
              }),
            },
          ],
          onError: [
            {
              target: "selectingPerson",
              actions: assign({
                errorMessage: ({ event }) => {
                  const error = event.error as Error;
                  return error?.message || "";
                },
                searchResults: [],
              }),
            },
          ],
        },
      },
      searchingPerson: {
        invoke: {
          src: "searchPersons",
          input: ({ context }) => ({
            searchText: context.searchText,
          }),
          onDone: [
            {
              target: "selectingPerson",
              actions: assign({
                searchResults: ({ event }) => event.output,
              }),
            },
          ],
          onError: [
            {
              target: "selectingPerson",
              actions: assign({
                errorMessage: ({ event }) => {
                  const error = event.error as Error;
                  return error?.message || "";
                },
                searchResults: [],
              }),
            },
          ],
        },
      },
    },
  });
};
